// src/hooks/useDeckStates.js

import { useState, useEffect, useRef } from 'react';
import { useSocket } from '../context/SocketContext';
import { debugPlaybackLog } from '../utils/debugPlayback';

function useDeckStates(device) {
  const socket = useSocket();
  const [deckA, setDeckA] = useState({});
  const [deckB, setDeckB] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [masterVolume, setMasterVolume] = useState(100);
  const [crossfadePosition, setCrossfadePosition] = useState(50);
  const [isMixing, setIsMixing] = useState(false);
  const [playerSettings, setPlayerSettings] = useState({
    controls: 0,
    autoplay: 1,
    loop: 0,
    modestbranding: 0,
    fs: 1,
    color: 'red',
    playsinline: 0,
    cc_load_policy: 1,
    cc_lang_pref: 'en',
    disablekb: 0,
    iv_load_policy: 1,
    hl: 'en',
  });
  const [interfaceMode, setInterfaceMode] = useState('suitestream');
  const [externalApp, setExternalApp] = useState(null);
  const lastSummaryRef = useRef(null);
  const deviceId = device?.id;

  useEffect(() => {
    setDeckA({});
    setDeckB({});
    setExternalApp(null);
    setInterfaceMode('suitestream');
    setIsPlaying(false);
    setIsMixing(false);
    lastSummaryRef.current = null;
    if (!socket || !deviceId) return;

    const handleDeviceStateUpdate = (data) => {
      const { deviceId: reportedDeviceId, state } = data;
      if (Number(reportedDeviceId) !== Number(deviceId) || !state || typeof state !== 'object') return;

      const summary = {
        deviceId,
        isPlaying: typeof state.isPlaying === 'boolean' ? state.isPlaying : undefined,
        isMixing: typeof state.isMixing === 'boolean' ? state.isMixing : undefined,
        deckAKey: `${state?.deckA?.type || ''}:${state?.deckA?.videoId || state?.deckA?.creativeId || ''}`,
        deckBKey: `${state?.deckB?.type || ''}:${state?.deckB?.videoId || state?.deckB?.creativeId || ''}`,
      };
      const last = lastSummaryRef.current;
      if (
        !last ||
        last.isPlaying !== summary.isPlaying ||
        last.isMixing !== summary.isMixing ||
        last.deckAKey !== summary.deckAKey ||
        last.deckBKey !== summary.deckBKey
      ) {
        debugPlaybackLog('deviceStateUpdate', summary);
        lastSummaryRef.current = summary;
      }

      // Channel/monitoring snapshots do not clear playback. Explicit null/empty
      // decks still clear content; a new device resets the cache above.
      if (Object.prototype.hasOwnProperty.call(state, 'deckA')) setDeckA(state.deckA || {});
      if (Object.prototype.hasOwnProperty.call(state, 'deckB')) setDeckB(state.deckB || {});
      if (typeof state.isPlaying === 'boolean') setIsPlaying(state.isPlaying);
      if (typeof state.isMuted === 'boolean') setIsMuted(state.isMuted);
      setMasterVolume((prev) =>
        typeof state.masterVolume === 'number' && !Number.isNaN(state.masterVolume)
          ? state.masterVolume
          : prev
      );
      if (typeof state.crossfadePosition === 'number') setCrossfadePosition(state.crossfadePosition);
      if (typeof state.isMixing === 'boolean') setIsMixing(state.isMixing);
      if (state.playerSettings) setPlayerSettings(state.playerSettings);
      if (Object.prototype.hasOwnProperty.call(state, 'interfaceMode')) setInterfaceMode(state.interfaceMode === 'external_app' ? 'external_app' : 'suitestream');
      if (Object.prototype.hasOwnProperty.call(state, 'externalApp')) setExternalApp(state.externalApp || null);
    };

    socket.on('deviceStateUpdate', handleDeviceStateUpdate);

    // Request the current device state when the hook is initialized
    socket.emit('requestDeviceState', { deviceId });

    return () => {
      socket.off('deviceStateUpdate', handleDeviceStateUpdate);
    };
  }, [socket, deviceId]);

  return {
    deckA,
    deckB,
    isPlaying,
    isMuted,
    masterVolume,
    crossfadePosition,
    isMixing,
    playerSettings,
    interfaceMode,
    externalApp,
    setDeckA,          // Add this
    setDeckB,          // Add this
    setIsPlaying,
    setIsMuted,
    setMasterVolume,
    setCrossfadePosition,
    setIsMixing,
    setPlayerSettings,
    setInterfaceMode,
    setExternalApp,
  };
}

export default useDeckStates;
